/**
 * E-007 header/footer HTML. Same classes and disclosure pattern as the LMS
 * Mako templates and brand/header/bsg-header.js. No role=menu. No forms.
 */
import * as icons from "./icons.js";

function esc(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function initials(name) {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.charAt(0) ?? "";
  const last = words.length > 1 ? words[words.length - 1].charAt(0) : "";
  return (first + last).toUpperCase();
}

function ext(item) {
  return item.external ? ' target="_blank" rel="noopener"' : "";
}

function arrowSpan() {
  return `<span class="bsg-hdr__rowarrow" aria-hidden="true">${icons.arrow}</span>`;
}

function newTab(href, label, newTabLabel) {
  const text = String(newTabLabel || "Open {name} in a new tab").replace("{name}", label);
  return `<a href="${esc(href)}" target="_blank" rel="noopener" class="bsg-navmenu__newtab" aria-label="${esc(text)}">${icons.arrow}</a>`;
}

function splitRow(href, label, local, chrome) {
  if (local) {
    return `<li class="bsg-navmenu__row"><a href="${esc(href)}" class="bsg-navmenu__link">${esc(label)}</a></li>`;
  }
  return `<li class="bsg-navmenu__row bsg-navmenu__row--split"><a href="${esc(href)}" class="bsg-navmenu__link">${esc(label)}</a>${newTab(href, label, chrome.newTab)}</li>`;
}

function avatar(user, large) {
  const cls = large ? "bsg-avatar bsg-avatar--lg" : "bsg-avatar";
  if (user.avatarUrl) {
    return `<span class="${cls}" aria-hidden="true"><img src="${esc(user.avatarUrl)}" alt="" width="72" height="72"></span>`;
  }
  return `<span class="${cls}" aria-hidden="true">${esc(initials(user.name))}</span>`;
}

function isDonate(item) {
  return Boolean(item?.donate || item?.action === "donate");
}

function donateAttrs(item) {
  return isDonate(item) ? ' data-donate-open aria-haspopup="dialog" aria-controls="donation-form"' : "";
}

function flyout(item, index) {
  const children = item.children || [];
  let html = `<ul class="bsg-hdr__flyout" id="bsg-nav-menu-${index}" data-menu-panel>`;
  children.forEach((child, childIndex) => {
    const noteId = `nav-note-${index}-${childIndex}`;
    const described = child.requires_login ? ` aria-describedby="${noteId}"` : "";
    const note = child.requires_login
      ? `<span id="${noteId}" class="bsg-hdr__note">${esc(item.loginNote || "Login required*")}</span>`
      : "";
    const extra = isDonate(child) ? "" : ext(child);
    html += `<li class="bsg-hdr__row"><a href="${esc(child.href)}" class="bsg-hdr__rowlink"${extra}${donateAttrs(child)}${described}><span class="bsg-hdr__rowlabel">${esc(child.label)}</span>${child.external && !isDonate(child) ? arrowSpan() : ""}</a>${note}</li>`;
  });
  return `${html}</ul>`;
}

function desktopNav(chrome) {
  const items = chrome.primaryNav || [];
  const list = items
    .map((item, index) => {
      if (item.children && item.children.length) {
        item.loginNote = chrome.loginNote;
        return `<li class="bsg-hdr__item" data-nav-item data-menu><button type="button" class="bsg-hdr__link" data-menu-trigger aria-expanded="false" aria-controls="bsg-nav-menu-${index}"><span class="bsg-hdr__label">${esc(item.label)}</span></button>${flyout(item, index)}</li>`;
      }
      return `<li class="bsg-hdr__item" data-nav-item><a href="${esc(item.href)}" class="bsg-hdr__link"${ext(item)}><span class="bsg-hdr__label">${esc(item.label)}</span></a></li>`;
    })
    .join("");
  return `<nav aria-label="${esc(chrome.primaryNavLabel)}" class="bsg-hdr__nav"><ul class="bsg-hdr__list">${list}</ul></nav>`;
}

function mobileNav(chrome) {
  const items = chrome.primaryNav || [];
  return items
    .map((item, index) => {
      if (item.children && item.children.length) {
        const kids = item.children
          .map((child, childIndex) => {
            const noteId = `mnav-note-${index}-${childIndex}`;
            const described = child.requires_login ? ` aria-describedby="${noteId}"` : "";
            const note = child.requires_login
              ? `<span id="${noteId}" class="bsg-hdr__note bsg-hdr__note--mobile">${esc(chrome.loginNote)}</span>`
              : "";
            const extra = isDonate(child) ? "" : ext(child);
            return `<li class="bsg-hdr__mrow-wrap"><a href="${esc(child.href)}" class="bsg-hdr__mrow"${extra}${donateAttrs(child)}${described}><span>${esc(child.label)}</span>${child.external && !isDonate(child) ? arrowSpan() : ""}</a>${note}</li>`;
          })
          .join("");
        return `<li data-nav-item><details class="bsg-hdr__mitem"><summary class="bsg-hdr__mrow bsg-hdr__msummary" data-nav-url="${esc(item.href)}"><span>${esc(item.label)}</span>${icons.chevron}</summary><ul class="bsg-hdr__msub">${kids}</ul></details></li>`;
      }
      return `<li data-nav-item><a href="${esc(item.href)}" class="bsg-hdr__mrow bsg-hdr__msummary"${ext(item)}><span>${esc(item.label)}</span></a></li>`;
    })
    .join("");
}

function switcher(chrome, variant) {
  const entries = [
    { id: "main", label: chrome.switcherMain, href: chrome.websiteUrl },
    { id: "learn", label: chrome.switcherLearn, href: chrome.learnUrl },
    { id: "shop", label: chrome.switcherShop, href: chrome.shopUrl },
  ];
  const current = entries[1];
  const rows = entries
    .map((entry) => {
      if (entry.id === "learn") {
        return `<li class="bsg-navmenu__row"><a href="${esc(entry.href)}" class="bsg-navmenu__link" aria-current="true">${icons.check}<span>${esc(entry.label)}</span></a></li>`;
      }
      return splitRow(entry.href, entry.label, false, chrome);
    })
    .join("");
  if (variant === "drawer") {
    return `<details class="bsg-switchdrawer"><summary>${esc(current.label)}${icons.chevron}</summary><ul class="bsg-navmenu__list">${rows}</ul></details>`;
  }
  const label = String(chrome.switcherLabel || "{current}, switch site").replace("{current}", current.label);
  return `<div class="bsg-util__menu" data-menu><button type="button" class="bsg-util__trigger bsg-switch__trigger" data-menu-trigger aria-expanded="false" aria-controls="bsg-switch-panel" aria-label="${esc(label)}"><span>${esc(current.label)}</span>${icons.chevron}</button><div class="bsg-navmenu" id="bsg-switch-panel" data-menu-panel><ul class="bsg-navmenu__list">${rows}</ul></div></div>`;
}

function accountRows(chrome) {
  const signedIn = chrome.user?.signedIn;
  const studio = chrome.studioUnifiedNav
    ? [
        splitRow(
          chrome.studioHomeUrl || "/authoring/home",
          chrome.studioHomeLabel || "Studio",
          true,
          chrome,
        ),
      ]
    : [];
  return [
    ...studio,
    splitRow(chrome.shopAccountUrl, chrome.shopAccountLabel, false, chrome),
    splitRow(chrome.dashboardUrl, chrome.learnDashboardLabel, true, chrome),
    splitRow(chrome.bsgidAccountUrl, chrome.bsgidAccountLabel, false, chrome),
    signedIn
      ? `<li class="bsg-navmenu__row bsg-navmenu__sep"><a href="${esc(chrome.logoutUrl)}" class="bsg-navmenu__link">${esc(chrome.signOutLabel)}</a></li>`
      : "",
  ].join("");
}

function account(chrome, variant) {
  const user = chrome.user || {};
  const signedIn = Boolean(user.signedIn);
  if (variant === "drawer") {
    const who = signedIn
      ? `<div class="bsg-acct__who">${avatar(user, true)}<div class="bsg-acct__ident"><p class="bsg-acct__name">${esc(user.name)}</p><p class="bsg-acct__email">${esc(user.email)}</p></div></div>`
      : `<a href="${esc(chrome.loginUrl)}" class="bsg-navmenu__link bsg-navmenu__link--strong">${esc(chrome.signInLabel)}</a>`;
    return `${who}<ul class="bsg-navmenu__list">${accountRows(chrome)}</ul>`;
  }
  const triggerLabel = signedIn
    ? String(chrome.accountSignedIn || "Account, {name}").replace("{name}", user.name)
    : chrome.accountSignedOut;
  const triggerInner = signedIn ? avatar(user, false) : icons.person;
  const who = signedIn
    ? `<div class="bsg-acct__who">${avatar(user, false)}<div class="bsg-acct__ident"><p class="bsg-acct__name">${esc(user.name)}</p><p class="bsg-acct__email">${esc(user.email)}</p></div></div>`
    : "";
  const signIn = signedIn
    ? ""
    : `<li class="bsg-navmenu__row"><a href="${esc(chrome.loginUrl)}" class="bsg-navmenu__link bsg-navmenu__link--strong">${esc(chrome.signInLabel)}</a></li>`;
  return `<div class="bsg-util__menu" data-menu><button type="button" class="bsg-util__trigger" data-menu-trigger aria-expanded="false" aria-controls="bsg-acct-panel" aria-label="${esc(triggerLabel)}">${triggerInner}</button><div class="bsg-navmenu" id="bsg-acct-panel" data-menu-panel>${who}<ul class="bsg-navmenu__list">${signIn}${accountRows(chrome)}</ul></div></div>`;
}

function studioSubbrandItems(chrome) {
  if (Array.isArray(chrome.studioSubbrandItems) && chrome.studioSubbrandItems.length) {
    return chrome.studioSubbrandItems;
  }
  return [
    {
      label: chrome.studioHomeNavLabel || "Studio Home",
      href: chrome.studioHomeUrl || "/authoring/home",
    },
    {
      label: chrome.studioLibrariesLabel || "Libraries",
      href: chrome.studioLibrariesUrl || "/authoring/libraries",
    },
    {
      label: chrome.learnDashboardLabel || "BSG Learn",
      href: chrome.dashboardUrl || "/dashboard",
    },
  ];
}

function subbrandItems(chrome) {
  if (chrome.studioUnifiedNav) return studioSubbrandItems(chrome);
  return chrome.subbrandItems || [];
}

function subbrand(chrome, variant) {
  const items = subbrandItems(chrome)
    .map((item) => `<li><a href="${esc(item.href)}" class="bsg-sub__link">${esc(item.label)}</a></li>`)
    .join("");
  const homeHref = chrome.studioUnifiedNav
    ? chrome.studioHomeUrl || "/authoring/home"
    : chrome.coursesUrl || "/";
  const home = `<a href="${esc(homeHref)}" class="bsg-sub__home"><img class="bsg-sub__logo" src="${esc(chrome.learnLogoUrl)}" alt="${esc(chrome.subbrandName)}" width="120" height="28"></a>`;
  const list = `<ul class="bsg-sub__list">${items}</ul>`;
  if (variant === "drawer") {
    return `<nav aria-label="${esc(chrome.subbrandName)}" class="bsg-subdrawer">${home}${list}</nav>`;
  }
  return `<nav aria-label="${esc(chrome.subbrandName)}" class="bsg-sub">${home}${list}</nav>`;
}

function studioUtility(chrome) {
  if (!chrome.studioUnifiedNav) return "";
  const searchHref = chrome.studioSearchUrl || chrome.studioHomeUrl || "/authoring/home";
  const bellLabel = chrome.studioNotificationsLabel || "Notifications";
  const searchLabel = chrome.studioSearchLabel || "Search";
  return `<div id="bsg-studio-utility" class="bsg-studio-util" data-studio-utility><button type="button" class="bsg-studio-fallback bsg-studio-fallback-bell" data-studio-fallback="bell" aria-label="${esc(bellLabel)}">${icons.bell}</button><a class="bsg-studio-fallback bsg-studio-fallback-search" data-studio-fallback="search" href="${esc(searchHref)}" aria-label="${esc(searchLabel)}">${icons.search}</a></div>
            `;
}

function donateConfig(chrome) {
  return `<div id="bsg-donate-config" hidden data-widget-src="${esc(chrome.donateWidgetSrc)}" data-donate-url="${esc(chrome.donateUrl)}" data-heading="${esc(chrome.donateHeading)}" data-blurb="${esc(chrome.donateBlurb)}" data-learn-more="${esc(chrome.donateLearnMore)}" data-close="${esc(chrome.donateClose)}" data-donate-now="${esc(chrome.donateNow)}"></div>`;
}

export function renderHeader(chrome) {
  const donate = `<a class="bsg-hdr__pill" href="${esc(chrome.donateUrl)}" data-donate-open aria-haspopup="dialog" aria-controls="donation-form">${esc(chrome.donateNow)}</a>`;
  return `<a class="bsg-skip" href="#main">${esc(chrome.skipLink)}</a>
<div class="bsg-chrome">
  <header class="bsg-hdr bsg-menuscope" data-site-header data-parity="header" data-scrolled="true" data-login-url="${esc(chrome.loginUrl)}">
    <div class="bsg-hdr__inner">
      <a class="bsg-hdr__logo" href="${esc(chrome.websiteUrl)}">
        <img class="bsg-hdr__logo-image" src="${esc(chrome.siteLogoUrl)}" alt="${esc(chrome.siteLogoAlt)}" width="174" height="40">
      </a>
      ${desktopNav(chrome)}
      <div class="bsg-hdr__utility">
        <div class="bsg-hdr__utility-inner">
          <div class="bsg-util" role="group" aria-label="${esc(chrome.utilityLabel)}">
            ${switcher(chrome, "bar")}
            <span class="bsg-util__pipe" aria-hidden="true"></span>
            ${studioUtility(chrome)}${account(chrome, "bar")}
          </div>
        </div>
      </div>
      <div class="bsg-hdr__cta">${donate}</div>
      <details class="bsg-hdr__disclosure">
        <summary class="bsg-hdr__toggle">
          ${icons.menu}${icons.close}
          <span class="sr-only">${esc(chrome.menuToggle)}</span>
        </summary>
        <span class="bsg-hdr__backdrop" aria-hidden="true"></span>
        <nav aria-label="${esc(chrome.mobileNavLabel)}" class="bsg-hdr__panel">
          <div class="bsg-utildrawer">
            ${account(chrome, "drawer")}
            ${switcher(chrome, "drawer")}
          </div>
          ${subbrand(chrome, "drawer")}
          <ul class="bsg-hdr__mlist">${mobileNav(chrome)}</ul>
          <div class="bsg-hdr__mcta">${donate}</div>
        </nav>
      </details>
    </div>
  </header>
  ${subbrand(chrome, "bar")}
</div>
${donateConfig(chrome)}`;
}

export function renderFooter(chrome) {
  const columns = (chrome.footerColumns || []).filter((col) => {
    if (col.scope === "learn") return chrome.showLearnColumn;
    return true;
  });
  const cols = columns
    .map((col) => {
      const links = (col.links || [])
        .map((link) => `<li><a href="${esc(link.url)}"${link.external ? ' target="_blank" rel="noopener"' : ""}>${esc(link.title)}</a></li>`)
        .join("");
      const scope = col.scope === "learn" ? ' data-scope="learn"' : "";
      return `<nav class="site-footer__col"${scope} aria-label="${esc(col.title)}"><p class="site-footer__heading">${esc(col.title)}</p><ul class="site-footer__links">${links}</ul></nav>`;
    })
    .join("");
  const legal = (chrome.footerLegal || [])
    .map((link) => `<a href="${esc(link.url)}">${esc(link.title)}</a>`)
    .join("");
  return `<div class="bsg-give"><footer class="site-footer" data-parity="footer" id="footer"><div class="site-footer__inner"><div class="site-footer__grid"><div class="site-footer__brand"><img class="site-footer__logo" src="${esc(chrome.footerMarkUrl)}" alt="${esc(chrome.siteLogoAlt)}" width="140" height="123"><p class="site-footer__tagline">${esc(chrome.footerNonprofit)}</p></div><div class="site-footer__columns">${cols}</div></div><div class="site-footer__bottom"><p>${esc(chrome.footerCopyright)}</p><div class="site-footer__legal-links">${legal}</div></div></div></footer></div>`;
}